import React, { useState } from "react";
import Card from "./Card";
import Button from "./Button";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import { Eye, EyeOff } from "lucide-react";
import { friendlyAuthError } from "../../utils/friendlyAuthError";

// Rendered when the user arrives back at the app via a Supabase password
// recovery email link. Supabase's client parses the hash token automatically
// and creates a short-lived recovery session; this component lets them set
// a new password and sign in as themselves. On success we clear the recovery
// hash so a refresh doesn't drop them back here.
export default function ResetPasswordPage({ onDone }) {
  const { showToast } = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    if (password.length < 6) {
      showToast("Password must be at least 6 characters.", "error");
      return;
    }
    if (password !== confirm) {
      showToast("Passwords don't match.", "error");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true);
      showToast("Password updated. You're signed in.", "success");
    } catch (err) {
      showToast(friendlyAuthError(err.message) || "Could not update password.", "error");
    } finally {
      setLoading(false);
    }
  }

  function handleContinue() {
    // Clear the recovery hash so the reset screen doesn't re-trigger if the
    // user refreshes the page. The active session (now authenticated via the
    // new password) stays put.
    if (window.location.hash) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
    onDone?.();
  }

  return (
    <div className="page">
      <div className="two-grid">
        <Card className="hero dark">
          <h1>Set a new password</h1>
          <p>
            You're signing back in via the reset link in your inbox. Choose a
            new password to continue.
          </p>
        </Card>

        <Card>
          <h2>{success ? "Password updated" : "Choose a new password"}</h2>

          {success ? (
            <>
              <p className="muted">
                Your password has been reset and you're signed in.
              </p>
              <div className="button-row">
                <Button onClick={handleContinue}>Continue</Button>
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="form-grid">
              <div className="full">
                <label>New password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="full">
                <label>Confirm password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter password"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                    tabIndex={-1}
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="full button-row">
                <Button type="submit" disabled={loading}>
                  {loading ? "Updating…" : "Update password"}
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
